import { ComponentType, lazy } from 'react';

const STORAGE_KEY = 'aq:chunk-retry';

interface RetryRecord {
  [chunkName: string]: number;
}

function readRetries(): RetryRecord {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}') as RetryRecord;
  } catch {
    return {};
  }
}

function writeRetries(record: RetryRecord): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // sessionStorage может быть недоступен (приватный режим / квота) — не критично.
  }
}

function resetRetry(chunkName: string): void {
  const record = readRetries();
  delete record[chunkName];
  writeRetries(record);
}

/**
 * Возвращает true, если ошибка связана с невозможностью загрузить кодовый чанк.
 * Браузеры формулируют это по-разному (Loading chunk / Loading CSS chunk /
 * Failed to fetch dynamically imported module), поэтому проверяем по нескольким
 * сигналам — и по сообщению, и по типу.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const message = (error as Error)?.message ?? '';
  return (
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Loading CSS chunk [\d]+ failed/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

/**
 * Обёртка над React.lazy с авторетраем при сбое загрузки чанка.
 *
 * Зачем: после нового деплоя у пользователя в браузере остаётся старый index.html
 * со ссылками на чанки по старым хэшам (или их закэшировал service worker).
 * При первой client-side навигации на маршрут React пытается загрузить чанк,
 * которого уже нет → бросается chunk-load-ошибка → белый экран (без ErrorBoundary).
 *
 * Стратегия: при первой такой ошибке делаем 1–2 ретрая с паузой. Браузер при этом
 * обычно успевает подтянуть свежий index.html, и повторная попытка находит чанк.
 * Счётчик попыток хранится в sessionStorage, чтобы избежать бесконечных ретраев.
 */
// React.lazy требует ComponentType<any> — любая замена на конкретный тип ломает
// все call sites из-за контравариантности пропсов. Подавляем осознанно.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  chunkName: string,
  maxRetries = 2
) {
  return lazy(() =>
    retry(factory, chunkName, maxRetries).finally(() => resetRetry(chunkName))
  );
}

async function retry<T>(
  factory: () => Promise<T>,
  chunkName: string,
  maxRetries: number
): Promise<T> {
  const record = readRetries();
  const attempt = record[chunkName] ?? 0;

  try {
    return await factory();
  } catch (error) {
    if (!isChunkLoadError(error) || attempt >= maxRetries) {
      throw error;
    }

    // Запоминаем номер попытки и ждём перед ретраем — этого часто достаточно,
    // чтобы браузер/сеть отдали свежий index.html с актуальными хэшами чанков.
    record[chunkName] = attempt + 1;
    writeRetries(record);

    await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
    return retry(factory, chunkName, maxRetries);
  }
}
